/**
 * Support Center Firebase Service
 * 
 * Handles all Firebase operations for support tickets.
 * Therapists can create tickets and add messages.
 * Admin (Niv) manages tickets via nivFirebase.js
 */

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  arrayUnion,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";

// ============================================================================
// CONSTANTS
// ============================================================================

export const SUPPORT_TICKET_STATUSES = {
  OPEN: "open",
  WAITING_ADMIN: "waiting_admin",
  WAITING_CUSTOMER: "waiting_customer",
  IN_PROGRESS: "in_progress",
  STUCK: "stuck",
  COMPLETED: "completed",
};

export const STATUS_LABELS = {
  open: "פתוח",
  waiting_admin: "ממתין למענה מנהל מערכת",
  waiting_customer: "ממתין למענה לקוח",
  in_progress: "בטיפול",
  stuck: "תקוע",
  completed: "הסתיים",
};

export const STATUS_COLORS = {
  open: "#3b82f6",           // Blue
  waiting_admin: "#f59e0b",  // Orange/Yellow
  waiting_customer: "#8b5cf6", // Purple
  in_progress: "#06b6d4",    // Cyan
  stuck: "#ef4444",          // Red
  completed: "#22c55e",      // Green
};

// ============================================================================
// THERAPIST FUNCTIONS
// ============================================================================

/**
 * Create a new support ticket
 * @param {Object} ticketData - Ticket data
 * @param {string} ticketData.clinicId - Clinic ID
 * @param {string} ticketData.therapistId - Therapist ID
 * @param {string} ticketData.therapistName - Therapist full name
 * @param {string} ticketData.clinicName - Clinic name
 * @param {string} ticketData.subject - Ticket subject
 * @param {string} ticketData.description - Ticket description
 * @param {string} [ticketData.category="general"] - Ticket category ("general" | "help_question")
 * @returns {Promise<Object>} Created ticket with ID
 */
export const createSupportTicket = async (ticketData) => {
  try {
    const { clinicId, therapistId, therapistName, clinicName, subject, description, category } = ticketData;

    if (!clinicId || !therapistId || !therapistName || !subject || !description) {
      throw new Error("Missing required fields for support ticket");
    }

    const ticketRef = collection(db, "supportTickets");
    
    const initialMessage = {
      id: `msg_${Date.now()}`,
      senderId: therapistId,
      senderName: therapistName,
      senderType: "therapist",
      content: description,
      createdAt: new Date().toISOString(),
    };

    const newTicket = {
      clinicId,
      therapistId,
      therapistName,
      clinicName: clinicName || "לא צוין",
      subject,
      category: category || "general",
      status: SUPPORT_TICKET_STATUSES.OPEN,
      messages: [initialMessage],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      closedAt: null,
    };

    const docRef = await addDoc(ticketRef, newTicket);

    return {
      id: docRef.id,
      ...newTicket,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
  } catch (error) {
    console.error("[CK-SUPPORT] Error creating support ticket:", error);
    throw error;
  }
};

/**
 * Get all support tickets for a therapist
 * @param {string} therapistId - Therapist ID
 * @returns {Promise<Array>} Array of tickets
 */
export const getTherapistSupportTickets = async (therapistId) => {
  try {
    if (!therapistId) {
      throw new Error("Missing therapistId");
    }

    const ticketsRef = collection(db, "supportTickets");
    const q = query(
      ticketsRef,
      where("therapistId", "==", therapistId),
      orderBy("updatedAt", "desc")
    );

    const snapshot = await getDocs(q);
    const tickets = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate?.() || new Date(),
      closedAt: doc.data().closedAt?.toDate?.() || null,
    }));

    return tickets;
  } catch (error) {
    console.error("[CK-SUPPORT] Error fetching therapist tickets:", error);
    throw error;
  }
};

/**
 * Get a single support ticket by ID
 * @param {string} ticketId - Ticket ID
 * @returns {Promise<Object|null>} Ticket data or null
 */
export const getSupportTicket = async (ticketId) => {
  try {
    if (!ticketId) {
      throw new Error("Missing ticketId");
    }

    const ticketRef = doc(db, "supportTickets", ticketId);
    const ticketSnap = await getDoc(ticketRef);

    if (!ticketSnap.exists()) {
      return null;
    }

    const data = ticketSnap.data();
    return {
      id: ticketSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate?.() || new Date(),
      updatedAt: data.updatedAt?.toDate?.() || new Date(),
      closedAt: data.closedAt?.toDate?.() || null,
    };
  } catch (error) {
    console.error("[CK-SUPPORT] Error fetching support ticket:", error);
    throw error;
  }
};

/**
 * Add a message to a support ticket (therapist)
 * When therapist sends a message, status changes to "waiting_admin"
 * @param {string} ticketId - Ticket ID
 * @param {Object} messageData - Message data
 * @param {string} messageData.senderId - Sender ID
 * @param {string} messageData.senderName - Sender name
 * @param {string} messageData.content - Message content
 * @returns {Promise<Object>} Updated ticket
 */
export const addTherapistMessage = async (ticketId, messageData) => {
  try {
    if (!ticketId || !messageData.senderId || !messageData.content) {
      throw new Error("Missing required fields for message");
    }

    const ticketRef = doc(db, "supportTickets", ticketId);
    
    const newMessage = {
      id: `msg_${Date.now()}`,
      senderId: messageData.senderId,
      senderName: messageData.senderName,
      senderType: "therapist",
      content: messageData.content,
      createdAt: new Date().toISOString(),
    };

    await updateDoc(ticketRef, {
      messages: arrayUnion(newMessage),
      status: SUPPORT_TICKET_STATUSES.WAITING_ADMIN,
      updatedAt: serverTimestamp(),
    });

    return getSupportTicket(ticketId);
  } catch (error) {
    console.error("[CK-SUPPORT] Error adding therapist message:", error);
    throw error;
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Format date for display
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatTicketDate = (date) => {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString("he-IL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Get ticket stats for a therapist
 * @param {Array} tickets - Array of tickets
 * @returns {Object} Stats object
 */
export const getTicketStats = (tickets) => {
  const stats = {
    total: tickets.length,
    open: 0,
    waitingAdmin: 0,
    waitingCustomer: 0,
    inProgress: 0,
    stuck: 0,
    completed: 0,
  };

  tickets.forEach((ticket) => {
    switch (ticket.status) {
      case SUPPORT_TICKET_STATUSES.OPEN:
        stats.open++;
        break;
      case SUPPORT_TICKET_STATUSES.WAITING_ADMIN:
        stats.waitingAdmin++;
        break;
      case SUPPORT_TICKET_STATUSES.WAITING_CUSTOMER:
        stats.waitingCustomer++;
        break;
      case SUPPORT_TICKET_STATUSES.IN_PROGRESS:
        stats.inProgress++;
        break;
      case SUPPORT_TICKET_STATUSES.STUCK:
        stats.stuck++;
        break;
      case SUPPORT_TICKET_STATUSES.COMPLETED:
        stats.completed++;
        break;
      default:
        break;
    }
  });

  return stats;
};
